import React, { useEffect } from "react";
import "../../App.scss";
import Footer from "./Footer";
import { Container } from "reactstrap";
import { Route, Routes, useLocation } from "react-router-dom";
import Login from "../login/Login";
import classNames from "classnames";
import loadable from "@loadable/component";
import HomePage from "../../pages/HomePage";
import ReleaseRulesPage from "../../pages/ReleaseRulesPage";
import BankStatementsPage from "../../pages/BankStatementsPage";
import CessionStatementsPage from "../../pages/CessionStatementsPage";
import RouteWhenMemberOfAny from "../general/RouteWhenMemberOfAny";

import InviteUserSteps from "../useradmin/InviteUserSteps";
import BankContactList from "../bankcontacts/BankContactList";
import ClientList from "../clients/ClientList";
import { useSelector, useDispatch } from "react-redux";
import { selectModuleConfig } from "../../features/moduleConfig/moduleConfigSlice";
import {
  selectOtherPages,
  selectProfileMenuPages,
  selectSideNavPages,
} from "../../features/pages/pagesSlice";
import { useAuthContext } from "../../Auth";
import { loadUser2FAStatus } from "../../features/users/user2FASlice";

const UserList = loadable(() => import("../useradmin/UserList"));
const UserProfile = loadable(() => import("../useradmin/UserProfile"));
const Support = loadable(() => import("../../pages/SupportPage"));

const ReportPage = loadable(() => import("../../pages/ReportPage"));
const ActivateUser = loadable(() => import("../login/ActivateUser"));

const BankAccounts = loadable(
  () => import("../../components/bankaccounts/BankAccounts"),
);

const TransferRequest = loadable(
  () => import("../../components/transferrequest/TransferRequest"),
);

const TransferRequestDetails = loadable(
  () => import("../../components/transferrequest/TransferRequestDetails"),
);

const SystemActivityPage = loadable(
  () => import("../../pages/SystemActivityPage"),
);

function Content(props) {
  const { sidebarIsOpen, excludeLocations } = props;
  const location = useLocation();
  let isActivate = excludeLocations.some(function (loc) {
    return location.pathname.startsWith(loc);
  });
  let isReportPage = location.pathname.startsWith("/report");
  const moduleConfig = useSelector(selectModuleConfig);
  const pages = [
    ...useSelector(selectSideNavPages),
    ...useSelector(selectProfileMenuPages),
    ...useSelector(selectOtherPages),
  ];

  const getPermissionGroupsForRoute = (path) => {
    const matchingPage = pages.find((p) => p.clientRoute === path);
    if (matchingPage) {
      return matchingPage.groups;
    } else {
      return moduleConfig.permissionGroups.map((g) => g.oktaName);
    }
  };

  const dispatch = useDispatch();
  const authContext = useAuthContext();
  const { isUserInAnyGroups } = useAuthContext();

  useEffect(() => {
    if (authContext.isAuthenticated) {
      dispatch(loadUser2FAStatus());
    }
  }, [dispatch, authContext.isAuthenticated]);

  return (
    <Container
      className={classNames(
        "content",
        { "is-open": sidebarIsOpen },
        { "report-page": isReportPage },
        "d-flex",
        "flex-wrap",
        "justify-content-center",
        "p-0",
        "mw-100",
      )}
    >
      <div
        className={
          !(excludeLocations.includes(location.pathname) || isActivate)
            ? isReportPage
              ? "w-100 pl-xs-4 pl-md-5 py-3 inner-content"
              : "w-100 px-xs-4 px-md-5 py-3 inner-content"
            : "w-100 p-0"
        }
      >
        {moduleConfig && (
          <>
            <RouteWhenMemberOfAny
              groups={getPermissionGroupsForRoute("/admin/banksandletters")}
              path="/admin/banksandletters"
              render={({ component }) => component()}
              component={BankContactList}
            />
            <RouteWhenMemberOfAny
              groups={getPermissionGroupsForRoute("/admin/users")}
              path="/admin/users"
              render={({ component }) => component()}
              component={UserList}
            />

            <RouteWhenMemberOfAny
              groups={getPermissionGroupsForRoute("/admin/clientinfo")}
              path="/admin/clientinfo"
              render={({ component }) => component()}
              component={ClientList}
            />

            <RouteWhenMemberOfAny
              groups={getPermissionGroupsForRoute("/admin/users")}
              path="/users/add"
              render={({ component }) => component()}
              component={InviteUserSteps}
            />

            <RouteWhenMemberOfAny
              groups={moduleConfig.permissionGroups.map((g) => g.oktaName)}
              path="/user-profile/:userId?"
              render={({ component }) =>
                component({
                  canAccessBankAccountsPage: isUserInAnyGroups(
                    getPermissionGroupsForRoute("/bankaccounts"),
                  ),
                  canAccessCessionStatementsPage: isUserInAnyGroups(
                    getPermissionGroupsForRoute("/cessionstatements"),
                  ),
                  canRequestTransfers: isUserInAnyGroups(
                    getPermissionGroupsForRoute("/transferrequest"),
                  ),
                  canAccessReleaseRulesPage: isUserInAnyGroups(
                    getPermissionGroupsForRoute("/releaserules"),
                  ),
                })
              }
              component={UserProfile}
            />

            <RouteWhenMemberOfAny
              groups={getPermissionGroupsForRoute("/admin/users")}
              path="/support"
              render={({ component }) => component()}
              component={Support}
            />

            <RouteWhenMemberOfAny
              groups={getPermissionGroupsForRoute("/")}
              path="/"
              render={({ component }) => component()}
              component={HomePage}
            />

            <RouteWhenMemberOfAny
              groups={getPermissionGroupsForRoute("/bankstatements")}
              path="/bankstatements"
              render={({ component }) => component()}
              component={BankStatementsPage}
            />

            <RouteWhenMemberOfAny
              groups={getPermissionGroupsForRoute("/cessionstatements")}
              path="/cessionstatements"
              render={({ component }) => component()}
              component={CessionStatementsPage}
            />

            <RouteWhenMemberOfAny
              groups={getPermissionGroupsForRoute("/releaserules")}
              path="/releaserules"
              render={({ component }) => component()}
              component={ReleaseRulesPage}
            />

            <RouteWhenMemberOfAny
              groups={getPermissionGroupsForRoute("/bankaccounts")}
              path="/bankaccounts"
              render={({ component }) => component()}
              component={BankAccounts}
            />

            <RouteWhenMemberOfAny
              groups={getPermissionGroupsForRoute("/transferrequest")}
              path="/transferrequest/:clientId/:cessionPeriodBalanceId"
              render={({ component }) => component()}
              component={TransferRequest}
            />

            <RouteWhenMemberOfAny
              groups={getPermissionGroupsForRoute("/transferrequest")}
              path="/transferrequest/:id"
              render={({ component }) => component()}
              component={TransferRequestDetails}
            />

            <RouteWhenMemberOfAny
              groups={getPermissionGroupsForRoute("/admin/systemactivity")}
              path="/admin/systemactivity"
              render={({ component }) => component()}
              component={SystemActivityPage}
            />

            <RouteWhenMemberOfAny
              groups={getPermissionGroupsForRoute("/report/balances_report")}
              path="/report/:reportId/:nav?"
              render={({ component }) => component()}
              component={ReportPage}
            />
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/activate/:activationToken?"
                element={<ActivateUser />}
              />
            </Routes>
          </>
        )}
      </div>

      <Footer
        sidebarIsOpen={sidebarIsOpen}
        excludeLocations={excludeLocations}
      />
    </Container>
  );
}

export default Content;
